import { useState } from "react";
import { api } from "@/lib/api";
import { bi } from "@/lib/i18n";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BadgeCheck, PenLine, XCircle, AlertTriangle } from "lucide-react";

const QS = ["q1", "q2", "q3"];
const UI = {
  tr: { title: "Niyet Mektubu Taslağı", none: "Öğrenci henüz taslak üretmedi.", q: ["1. Akademik Motivasyon", "2. Akademik Hazırlık", "3. Eğitim Dışı Deneyimler"], reviewed: "İncelendi olarak işaretle", approvedBy: "İncelendi ve onaylandı", stale: "Taslak onaydan sonra yeniden üretildi — tekrar inceleme gerekiyor.", note: "Onay notu (isteğe bağlı, PDF'e eklenir)", notePh: "Örn. Güçlü taslak; 2. soruda proje detayını genişletin.", remove: "Onayı kaldır", nameReq: "Lütfen adınızı girin (yukarıdaki alan)", done: "Onay kaydedildi", removed: "Onay kaldırıldı", chars: "karakter" },
  en: { title: "Personal Statement Draft", none: "The student has not generated a draft yet.", q: ["1. Academic Motivation", "2. Academic Preparation", "3. Outside Education"], reviewed: "Mark as reviewed", approvedBy: "Reviewed and approved by", stale: "The draft was regenerated after approval — needs a new review.", note: "Approval note (optional, printed on the PDF)", notePh: "e.g. Strong draft; expand the project detail in question 2.", remove: "Remove approval", nameReq: "Please enter your name (field above)", done: "Approval saved", removed: "Approval removed", chars: "characters" },
};

export const AdvisorApprovalPanel = ({ token, cv, lang, author, onChange }) => {
  const u = UI[lang];
  const answers = cv.statement?.answers || {};
  const texts = QS.map((q) => bi(answers[q], lang));
  const hasDraft = texts.some(Boolean);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const ap = cv.approval;
  const valid = cv.approvalValid;

  const approve = async () => {
    if (!author.trim()) return toast.error(u.nameReq);
    setBusy(true);
    try {
      const { data } = await api.post(`/shared/${token}/approve`, { author: author.trim(), note: note.trim() });
      onChange(data.approval, true); setNote(""); toast.success(u.done);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/shared/${token}/approve`); onChange(null, false); toast.success(u.removed); }
    catch { toast.error("Error"); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="advisor-statement-panel" className="bg-white rounded-xl border border-[#e2e8f0] p-5 mt-6">
      <div className="flex items-center gap-2 mb-3"><PenLine className="h-5 w-5 text-[#c0392b]" /><h2 className="font-display font-bold text-xl text-[#1a2e4a]">{u.title}</h2></div>
      {!hasDraft ? <p data-testid="advisor-statement-none" className="text-sm text-[#94a3b8] italic">{u.none}</p> : (
        <>
          <div className="grid md:grid-cols-3 gap-3">
            {QS.map((q, i) => (
              <div key={q} className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-3">
                <div className="text-[12px] font-bold text-[#1a2e4a] mb-1">{u.q[i]}</div>
                <p data-testid={`advisor-statement-${q}`} className="text-[12.5px] text-[#334155] whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto thin-scroll">{texts[i] || "—"}</p>
                <div className="text-[10px] text-[#94a3b8] mt-1">{texts[i].length} {u.chars}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-[#1a2e4a]/20 bg-[#eef2f8]/60 p-4">
            {ap && valid ? (
              <div data-testid="approval-stamp" className="flex flex-wrap items-start gap-3">
                <BadgeCheck className="h-6 w-6 text-green-600 shrink-0" />
                <div className="flex-1 text-sm text-[#1a2e4a]">
                  <div className="font-bold">{u.approvedBy}: {ap.author} · {String(ap.approvedAt).slice(0, 10)}</div>
                  {ap.note && <div className="text-[13px] text-[#475569] italic mt-0.5">{ap.note}</div>}
                </div>
                <Button data-testid="approval-remove-btn" size="sm" variant="outline" disabled={busy} onClick={remove} className="border-[#c0392b] text-[#c0392b]"><XCircle className="h-3.5 w-3.5 mr-1" /> {u.remove}</Button>
              </div>
            ) : (
              <>
                {ap && !valid && <div data-testid="approval-stale" className="flex items-center gap-2 text-[13px] text-amber-700 mb-3"><AlertTriangle className="h-4 w-4" /> {u.stale} ({ap.author}, {String(ap.approvedAt).slice(0, 10)})</div>}
                <Label className="text-[13px] text-[#1a2e4a]">{u.note}</Label>
                <Textarea data-testid="approval-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={u.notePh} className="mt-1 bg-white" />
                <Button data-testid="approval-btn" disabled={busy} onClick={approve} className="mt-3 bg-green-700 hover:bg-green-800 text-white"><BadgeCheck className="h-4 w-4 mr-1.5" /> {u.reviewed}</Button>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
};
